/**
 * Resolve a WA message's canonical serialized id — robust to the current
 * WhatsApp Web build where `MsgKey._serialized` is `undefined`.
 *
 * Observed (2026-07): the top-level message key no longer exposes
 * `_serialized` (returns undefined for every Store.Msg model), but the
 * MsgKey object still stringifies to the full serialized form
 * (`<fromMe>_<remote>_<id>[_<participant>]`) via its `toString`. Nested
 * wids (`id.remote._serialized`, `from._serialized`) are UNAFFECTED — only
 * the top-level key regressed. So the single fix everywhere the vault keys
 * a message is: prefer `_serialized`, else `String(id)`.
 *
 * Without this, the sweep/eligibility `if (!msgId) return false` guard
 * rejected 100% of messages and the vault captured nothing.
 */
export const resolveWaMsgSerializedId = (msg: any): string => {
  const ser = msg?.id?._serialized;
  if (ser) return String(ser);
  // Fallback: the MsgKey stringifies to the serialized key. Guard the
  // default Object stringification so a plain `{}` never becomes an id.
  if (msg?.id != null) {
    const s = String(msg.id);
    if (s && s !== '[object Object]') return s;
  }
  // Legacy webhook shape sometimes carries a flat `messageId`.
  if (msg?.messageId) return String(msg.messageId);
  return '';
};

/**
 * Addressing-agnostic message identity: `<fromMe>_<stanzaId>`.
 *
 * A serialized WA key is `<fromMe>_<chatJid>_<stanzaId>[_<participant>]`. The
 * SAME 1:1 message is delivered under two chat JIDs — the phone `@c.us` and the
 * linked-id `@lid` — so the full keys differ but the stanza id (segment [2],
 * unique per message) is identical. The vault + server backup dedup on this
 * canonical key so those two addressings collapse to a single stored row.
 * Falls back to the full id for malformed keys (never over-collapses).
 */
export const canonicalMsgKey = (serializedId: string): string => {
  if (typeof serializedId !== 'string' || serializedId.length === 0) return '';
  const parts = serializedId.split('_');
  if (parts.length < 3 || !parts[2]) return serializedId;
  return `${parts[0]}_${parts[2]}`;
};
